import "./_style.less";
import Paragraph from "antd/lib/typography/Paragraph";
import { useTranslation } from "react-i18next";
import { FC, useMemo } from "react";
import { RadioProps, Space } from "antd";
import moment from "moment";
import { Patient } from "../../../../models/Patient";
import clsx from "clsx";
import { useAppSelector } from "../../../../stores/hooks";
import { UserAddOutlined } from "@ant-design/icons";
import { VuiAvatar } from "../../../../@vendor/components";

interface IPatientCard {
  data: Patient;
  onSelect?: (v: Patient | null) => void;
  onCompleteProfile: () => void;
  isSelected?: boolean;
}

const PatientCard: FC<IPatientCard & RadioProps> = (props) => {
  const { data, onSelect, onCompleteProfile, isSelected = false } = props;
  const { account } = useAppSelector((state) => state.system);
  const { t } = useTranslation();

  const handleCompleteProfile = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onCompleteProfile();
  };

  const isNotCompleteData = useMemo(() => {
    return (
      account?.id === data?.user_id &&
      (!data?.name || !data?.address || !data?.date_of_birth || !data.gender)
    );
  }, [account, data]);

  const isMySelf = useMemo(() => {
    return account?.customer_id === data?.id;
  }, [account, data]);

  const getStatus = () => {
    if (isNotCompleteData) {
      return { className: "status-incomplete", text: t("common.text.incomplete") };
    }
    if (isMySelf) {
      return { className: "status-myself", text: t("common.text.myself") };
    }
    return { className: "status-complete", text: t("common.text.complete") };
  };

  const status = getStatus();

  const name = useMemo(() => data?.name || "-", [data]);

  const dateOfBirth = useMemo(
    () => (data?.date_of_birth ? moment(data.date_of_birth).format("DD MMMM YYYY") : "-"),
    [data]
  );

  const age = useMemo(() => {
    if (data?.date_of_birth) {
      const birthDate = moment(data.date_of_birth);
      return moment().diff(birthDate, "years");
    }
    return "-";
  }, [data]);

  const email = useMemo(() => data?.email || "-", [data]);

  const textStyle = isNotCompleteData ? { color: "#A7ABB1" } : {};

  return (
    <div
      className={clsx("patient-card-new", { selected: isSelected })}
      onClick={() => {
        if (!isNotCompleteData) {
          onSelect?.(isSelected ? null : data);
        }
      }}
      style={{ cursor: isNotCompleteData ? "not-allowed" : "pointer" }}
    >
      <div className="patient-card-content-wrapper">
        <div className={`patient-card-status-wrapper ${status.className}`}>
          {status.text}
        </div>

        <div className="patient-card-info-wrapper">
          <VuiAvatar
            className="patient-card-image"
            src={data?.photo?.url}
            alt={data?.name}
          />
          <Space direction="vertical" size={6}>
            <Paragraph className="patient-card-name" style={textStyle}>
              {name}
            </Paragraph>
            <Space direction="vertical" size={2}>
              <Paragraph className="patient-card-date" style={textStyle}>
                {dateOfBirth} ({age} Years)
              </Paragraph>
              <Paragraph className="patient-card-email" style={textStyle}>
                {email}
              </Paragraph>
            </Space>
          </Space>
        </div>

        <div className="patient-card-action-wrapper">
          {isNotCompleteData && (
            <>
              <UserAddOutlined style={{ fontSize: 14, color: "#FC3F93" }} />
              <button
                className="plain-btn complete-profile-btn"
                onClick={handleCompleteProfile}
              >
                Complete Profile
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientCard;
